import { ref, watch } from 'vue'
import { api } from '@/services/api.js'
import { useConfigFlag } from '@/composables/useConfigFlag.js'
import { ROTA_DAYS, PERIODS } from '@/constants/rota.js'

/**
 * Transform DB shift-centric data into the rep-centric format
 * that WeeklySchedule expects (matching the Google Sheet structure).
 *
 * @param {Array} shifts - DB shifts with assignedReps arrays
 * @param {Array} repInfoList - From /api/repData (Allocations rows: GBS ID, Name, Week Remit, tenant, ...)
 * @returns {Array} allRepsData in rota-sheet format (User ID, Name, Rep Sheet Info - Remit, day/period slots)
 */
function transformShiftsToRepFormat(shifts, repInfoList) {
  const repMap = new Map()

  // Collect all repIds referenced in shifts
  const allRepIds = new Set()
  shifts.forEach(s => s.assignedReps.forEach(id => allRepIds.add(id)))

  // Initialize a rota-format entry for each rep
  for (const repId of allRepIds) {
    const info = repInfoList.find(r => String(r['GBS ID']) === String(repId))

    const entry = {
      'User ID': repId,
      Name: info?.Name || 'Unknown',
      'Rep Sheet Info - Remit': info?.['Week Remit'] || '',
      tenant: info?.tenant || '',
      'Staff Type': info?.['Staff Type'] || info?.['Week Role'] || '',
    }

    // Initialize day/period structure
    ROTA_DAYS.forEach(day => {
      entry[day] = {}
      PERIODS.forEach(period => {
        entry[day][period] = {}
      })
    })

    repMap.set(repId, entry)
  }

  // Fill in shift data per rep
  for (const shift of shifts) {
    const dayName = ROTA_DAYS[shift.dayIndex]
    const periodName = PERIODS[shift.period]
    if (!dayName || !periodName) continue

    for (const repId of shift.assignedReps) {
      const rep = repMap.get(repId)
      if (!rep) continue

      rep[dayName][periodName] = {
        Role: shift.role,
        Location: shift.location,
        'URL or Lat/Long': shift.locationUrl,
        Start: shift.start,
        End: shift.end,
      }
    }
  }

  return Array.from(repMap.values())
}

/**
 * Composable that abstracts rota schedule data loading.
 * When the 'rota-editor-enabled' config flag is ON, loads from the DB API.
 * When OFF, loads from the legacy Google Sheet JSON.
 *
 * Returns { allRepsData, repData, loading } in the same shape
 * that WeeklySchedule and other components expect.
 */
export function useRotaData(weekId, repId, repTenant) {
  const allRepsData = ref([])
  const repData = ref(null)
  const loading = ref(true)

  const { enabled: rotaEnabled, loading: flagLoading } = useConfigFlag('rota-editor-enabled')

  async function loadFromSheet() {
    const tenant = repTenant.value
    let docType = 'rep'
    if (tenant === 'wasteland') {
      docType = 'wl-rep'
    }

    const response = await api.get(`/api/week/${docType}/${weekId}`)
    try {
      allRepsData.value = response.weekDocs[0].lastJsonData.jsonData
    } catch (e) {
      console.error('[useRotaData] Failed to parse sheet data:', e)
      allRepsData.value = []
    }
    repData.value = allRepsData.value.find(r => String(r['User ID']) === String(repId)) || null
  }

  async function loadFromDb(repInfoList) {
    const brand = repTenant.value === 'wasteland' ? 'wasteland' : 'nuco'
    const shifts = await api.get(`/api/rota?weekId=${weekId}&brand=${brand}`)

    if (Array.isArray(shifts) && shifts.length > 0) {
      allRepsData.value = transformShiftsToRepFormat(shifts, repInfoList)
    } else {
      allRepsData.value = []
    }
    repData.value = allRepsData.value.find(r => String(r['User ID']) === String(repId)) || null
  }

  /**
   * Call this after repTenant is set and repInfoList is available.
   * @param {Array} repInfoList - from /api/repData/:weekId
   */
  async function load(repInfoList) {
    loading.value = true
    try {
      // Wait for flag to resolve if still loading
      if (flagLoading.value) {
        await new Promise(resolve => {
          const stop = watch(flagLoading, (val) => {
            if (!val) {
              stop()
              resolve()
            }
          })
        })
      }

      if (rotaEnabled.value) {
        await loadFromDb(repInfoList)
      } else {
        await loadFromSheet()
      }
    } catch (err) {
      console.error('[useRotaData] Failed to load rota data:', err)
      allRepsData.value = []
      repData.value = null
    } finally {
      loading.value = false
    }
  }

  return { allRepsData, repData, loading, load, rotaEnabled }
}
